import { Fragment } from 'react';
import type { Environment } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { RowActions, type RowAction } from '@/components/RowActions';
import { EnvPill } from '@/components/badges';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EnvironmentForm } from './EnvironmentForm';
import { Forks, Patterns, ProtectionChip } from './EnvironmentCells';
import type { EnvironmentListProps } from './EnvironmentsPhoneList';

/** Table layout: one row per live environment, the editor opening in a full-width row under it. */
export function EnvironmentsTable(props: EnvironmentListProps) {
  return (
    <div className="@container">
      <Table aria-label="Environments" className="[&_td]:px-3 [&_th]:px-3 sm:[&_td:first-child]:pl-5 sm:[&_th:first-child]:pl-5 sm:[&_td:last-child]:pr-5 sm:[&_th:last-child]:pr-5">
        <EnvironmentsTableHead />
        <TableBody>
          {props.live.map((env, i) => (
            <EnvironmentRows key={env.id} env={env} index={i} {...props} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EnvironmentsTableHead() {
  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead>Environment</TableHead>
        <TableHead className="@max-[40rem]:hidden">Protected</TableHead>
        <TableHead>Allowed branches</TableHead>
        <TableHead className="whitespace-normal">Forks &amp; untrusted refs</TableHead>
        <TableHead className="w-0 text-right">
          <span className="sr-only">Actions</span>
        </TableHead>
      </TableRow>
    </TableHeader>
  );
}

function EnvironmentRows({ env, index, project, editing, setEditing, actionsFor }: EnvironmentListProps & { env: Environment; index: number }) {
  const isEditing = editing === env.id;
  return (
    <Fragment>
      <EnvironmentRow env={env} index={index} isEditing={isEditing} actions={actionsFor(env)} />
      {isEditing ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="whitespace-normal p-0!">
            <EnvironmentForm key={env.id} project={project} environment={env} onDone={() => setEditing(null)} />
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
}

function EnvironmentRow({ env, index, isEditing, actions }: { env: Environment; index: number; isEditing: boolean; actions: RowAction[] }) {
  return (
    <TableRow data-testid={`env-row-${env.slug}`} data-editing={isEditing || undefined} style={stagger(index)} className={cn('enter', isEditing && 'bg-secondary/60 hover:bg-secondary/60')}>
      <TableCell className="py-3">
        <span className="flex flex-col items-start gap-1">
          <EnvPill env={{ ...env, name: env.slug }} />
          {/* Narrow cards fold the Protected column into this cell. */}
          <span className="hidden @max-[40rem]:block">
            <ProtectionChip env={env} />
          </span>
        </span>
      </TableCell>
      <TableCell className="py-3 @max-[40rem]:hidden">
        <ProtectionChip env={env} plain />
      </TableCell>
      <TableCell className="whitespace-normal py-3">
        <Patterns env={env} />
      </TableCell>
      <TableCell className="py-3">
        <Forks env={env} />
      </TableCell>
      <TableCell className="py-3 text-right">
        {isEditing ? <span className="px-2 text-[13px] text-subtle">Editing…</span> : <RowActions actions={actions} subject={env.slug} testId={`env-actions-${env.slug}`} className="-my-1" />}
      </TableCell>
    </TableRow>
  );
}
